import { Chip } from '@mui/material';

/**
 * What a scratch card or promocode is bound to. The admin scratchcard list gets
 * `target_type` / `product_label` straight from the backend; everything else
 * (student profile tab, promocodes, the instructor portal) holds Lucid models
 * and derives them here, so both paths render identically.
 */
export type CardTargetType =
	| 'bundle'
	| 'standalone_course'
	| 'course'
	| 'classroom'
	| 'instructor'
	| null;

const TARGET_LABELS: Record<Exclude<CardTargetType, null>, string> = {
	bundle: 'Bundle',
	standalone_course: 'Standalone Course',
	course: 'Course',
	classroom: 'Classroom',
	instructor: 'Instructor-wide',
};

const TARGET_COLORS: Record<
	Exclude<CardTargetType, null>,
	'primary' | 'secondary' | 'success' | 'warning' | 'info'
> = {
	bundle: 'secondary',
	standalone_course: 'info',
	course: 'primary',
	classroom: 'success',
	instructor: 'warning',
};

type TargetSource = {
	target_type?: CardTargetType;
	bundle_id?: number | null;
	standalone_course_id?: number | null;
	course_id?: number | null;
	classroom_id?: number | null;
	instructor_id?: number | null;
};

/**
 * Most specific binding first. An unused instructor card reads as
 * 'Instructor-wide'; once redeemed it carries the product it was spent on,
 * which is what an admin looking at a used card wants to see.
 */
export const resolveTargetType = (row: TargetSource): CardTargetType => {
	if (row.target_type !== undefined) return row.target_type;
	if (row.bundle_id) return 'bundle';
	if (row.standalone_course_id) return 'standalone_course';
	if (row.course_id) return 'course';
	if (row.classroom_id) return 'classroom';
	if (row.instructor_id) return 'instructor';
	return null;
};

type RelationSource = TargetSource & {
	product_label?: string | null;
	bundle?: { title?: string } | null;
	standaloneCourse?: { title?: string } | null;
	course?: { name?: string } | null;
	classroom?: { title?: string } | null;
	instructor_label?: string | null;
};

/**
 * For rows that arrive as Lucid models with preloaded relations (student profile
 * tab, instructor portal) rather than the admin list's flat SQL projection.
 * Prefers a backend-computed `product_label` when one is present.
 */
export const resolveProductLabel = (row: RelationSource): string | null => {
	if (row.product_label !== undefined) return row.product_label;
	switch (resolveTargetType(row)) {
		case 'bundle':
			return row.bundle?.title ?? null;
		case 'standalone_course':
			return row.standaloneCourse?.title ?? null;
		case 'course':
			return row.course?.name ?? null;
		case 'classroom':
			return row.classroom?.title ?? null;
		case 'instructor':
			return row.instructor_label ?? null;
		default:
			return null;
	}
};

export const CardTargetChip = ({
	target,
	label,
}: {
	target: CardTargetType;
	/** Override the default label (e.g. "All my products" for instructor rows). */
	label?: string;
}) => {
	if (!target) return <>-</>;
	return (
		<Chip
			size="small"
			variant="outlined"
			color={TARGET_COLORS[target]}
			label={label ?? TARGET_LABELS[target]}
		/>
	);
};
